import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import {
  ACTION_LABELS,
  canRewindDealTrackerToStep,
  DEAL_STATUS,
  DEAL_TRACKER_BY_USER_TYPE,
  DealStatus,
  getDealTrackerVisualIndex,
  highlightDealTrackerNextStep,
  showHalfLineAfterCurrentStep,
  UserType,
} from '../constants/dealTracker';
import dealSignedOffImage from '../assets/Deal-Signed-off.png';
import dealSignedOnImage from '../assets/Deal-Signed-on.png';
import Button from './Button';

interface DealTrackerProps {
  userType: UserType;
  status: DealStatus;
  primaryActionLabel?: string | null;
  onPrimaryActionClick?: () => void;
  primaryActionLoading?: boolean;
  primaryActionDisabled?: boolean;
  primaryActionTooltip?: string;
  /** Smaller Follow button on the phone dashboard. */
  dense?: boolean;
  onPrimaryActionMouseEnter?: () => void;
  onPrimaryActionMouseLeave?: () => void;
  /** Click a completed earlier step to move the tracker back. */
  onCompletedStepClick?: (status: DealStatus) => void;
}

const FOLLOW_HOVER = 'Follow progress and save to Favorites for easy access';

const CIRCLE_PX = 18;
const DEAL_SIGNED_PX = 52;
/** Pending checkmark disc + unfinished connector line (must stay identical). */
const CHECKMARK_GREY = '#696969';
/** Completed check — PRAIRI emerald-500. Never use default Tailwind emerald. */
const CHECKMARK_GREEN = '#92d050';
const TRACKER_LINE_GREY = CHECKMARK_GREY;
// Icon row height matches the largest icon (Deal Signed badge) so all icons share the same vertical centre.
const ICON_ROW_PX = DEAL_SIGNED_PX;

/** Two-line breaks for labels that otherwise collide in the narrow overlay. */
const STEP_LABEL_LINES: Record<string, [string, string]> = {
  'Investor interest': ['Investor', 'interest'],
  'Investor Interest': ['Investor', 'Interest'],
  'Contact with investor': ['Contact with', 'investor'],
  'Contact with Investor': ['Contact with', 'Investor'],
  'Meet with them': ['Meet with', 'them'],
  'Deal in progress': ['Deal in', 'progress'],
};

const StepLabelText: React.FC<{ label: string }> = ({ label }) => {
  const parts = STEP_LABEL_LINES[label];
  if (!parts) return <>{label}</>;
  return (
    <>
      {parts[0]}
      <br />
      {parts[1]}
    </>
  );
};

/**
 * Same shape as the charcoal check PNG: filled disc with a transparent tick cutout.
 * Pending = #696969. Completed = PRAIRI green #92d050.
 */
const TrackerCheck: React.FC<{ completed?: boolean }> = ({ completed = false }) => {
  const fill = completed ? CHECKMARK_GREEN : CHECKMARK_GREY;
  const maskId = `tracker-tick-mask-${React.useId().replace(/:/g, '')}`;

  return (
    <svg
      viewBox="0 0 24 24"
      width="100%"
      height="100%"
      aria-hidden
      className="block overflow-visible"
    >
      <defs>
        <mask id={maskId}>
          <rect width="24" height="24" fill="white" />
          <path
            d="M7.2 12.5 L10.3 15.5 L16.8 8.6"
            fill="none"
            stroke="black"
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </mask>
      </defs>
      <circle cx="12" cy="12" r="11" fill={fill} mask={`url(#${maskId})`} />
    </svg>
  );
};

const DealSignedBadge: React.FC<{ completed?: boolean }> = ({ completed = false }) => (
  <div
    role="img"
    aria-label="Deal Signed"
    style={{
      width: DEAL_SIGNED_PX,
      height: DEAL_SIGNED_PX,
      backgroundImage: `url(${completed ? dealSignedOnImage : dealSignedOffImage})`,
      backgroundSize: 'contain',
      backgroundRepeat: 'no-repeat',
      backgroundPosition: 'center',
    }}
  />
);

const DealTracker: React.FC<DealTrackerProps> = ({
  userType,
  status,
  primaryActionLabel,
  onPrimaryActionClick,
  primaryActionLoading = false,
  primaryActionDisabled = false,
  primaryActionTooltip,
  dense = false,
  onPrimaryActionMouseEnter,
  onPrimaryActionMouseLeave,
  onCompletedStepClick,
}) => {
  const config = DEAL_TRACKER_BY_USER_TYPE[userType];
  const stepCount = config.order.length;

  const isAllCompleted = status === config.order[stepCount - 1];

  // Map the current backend status to a visual step index using this tracker's own order.
  // A backend status that isn't directly in the order (e.g. 'met' for investors) is mapped
  // to the highest visual step whose global position is ≤ the current status's global position.
  const currentIndex = getDealTrackerVisualIndex(status, userType);
  const highlightNextStep = highlightDealTrackerNextStep(status);
  const halfLineAfterCurrent = showHalfLineAfterCurrentStep(status);

  const resolvedLabel = primaryActionLabel ?? ACTION_LABELS[userType][status];
  const buttonLabel = resolvedLabel ?? 'Follow';
  const showButton = onPrimaryActionClick && resolvedLabel != null;
  const showFollowHover = buttonLabel === 'Follow';
  const [followTip, setFollowTip] = useState<{ top: number; left: number } | null>(null);

  // Step i is fully green when its status has been reached (i <= currentIndex).
  // The line into the next step is half green. That circle stays the unchecked grey.
  const isStepCompleted = (i: number) => isAllCompleted || i <= currentIndex;

  const stepHalfPx = (i: number) =>
    config.ui[config.order[i]]?.isFinalMilestone ? DEAL_SIGNED_PX / 2 : CIRCLE_PX / 2;

  const isStartup = userType === 'startup';

  // For startup view, ensure the first step text ("Investor interest") is not clipped on the left
  const edgeInset = isStartup ? 38 : DEAL_SIGNED_PX / 2;
  /** Evenly spaced centres between equal left/right insets. */
  const stepCenterLeft = (i: number) =>
    stepCount <= 1
      ? `50%`
      : `calc(${edgeInset}px + (100% - ${edgeInset * 2}px) * ${i / (stepCount - 1)})`;

  return (
    <div className="w-full pb-1">
      {/*
        Icons span nearly full width (wider than the 92% action button), with matching
        left/right insets. Labels stay centered under each tick.
      */}
      <div
        className={
          isStartup
            ? 'relative w-full'
            : 'relative -mx-2 w-[calc(100%+1rem)] sm:-mx-4 sm:w-[calc(100%+2rem)] lg:-mx-5 lg:w-[calc(100%+2.5rem)]'
        }
        role="list"
        aria-label="Deal progress"
      >
        {/* Connector lines behind the icons */}
        <div
          className="absolute left-0 right-0 z-0 pointer-events-none"
          style={{ top: isStartup ? 37 : ICON_ROW_PX / 2 - 1, height: 2 }}
          aria-hidden
        >
          {config.order.slice(0, -1).map((step, i) => {
            const lineAfterIsGreen = isStepCompleted(i + 1);
            const lineIsHalf =
              halfLineAfterCurrent && !isAllCompleted && currentIndex >= 0 && i === currentIndex;
            const fromHalf = stepHalfPx(i);
            const toHalf = stepHalfPx(i + 1);
            const span = stepCount <= 1 ? 0 : 1 / (stepCount - 1);

            return (
              <div
                key={`line-${step}`}
                data-line={lineIsHalf ? 'half' : lineAfterIsGreen ? 'full' : 'empty'}
                className="absolute top-0 h-full"
                style={{
                  left: `calc(${edgeInset}px + (100% - ${edgeInset * 2}px) * ${i * span} + ${fromHalf}px)`,
                  width: `calc((100% - ${edgeInset * 2}px) * ${span} - ${fromHalf + toHalf}px)`,
                }}
              >
                {lineIsHalf ? (
                  <>
                    <div
                      className="absolute left-0 top-0 h-full w-1/2"
                      style={{ backgroundColor: CHECKMARK_GREEN }}
                    />
                    <div
                      className="absolute left-1/2 top-0 h-full right-0"
                      style={{ backgroundColor: TRACKER_LINE_GREY }}
                    />
                  </>
                ) : (
                  <div
                    className="absolute inset-0 transition-colors duration-500"
                    style={{
                      backgroundColor: lineAfterIsGreen ? CHECKMARK_GREEN : TRACKER_LINE_GREY,
                    }}
                  />
                )}
              </div>
            );
          })}
        </div>

        <div className="relative z-10 w-full" style={{ height: isStartup ? 76 : ICON_ROW_PX + 28 }}>
          {config.order.map((step, i) => {
            const stepUi = config.ui[step];
            if (!stepUi) return null;

            const isCompleted = isStepCompleted(i);
            const isCurrent =
              highlightNextStep && !isAllCompleted && currentIndex >= 0 && i === currentIndex + 1;
            const isPending =
              !isAllCompleted && i > (highlightNextStep ? currentIndex + 1 : currentIndex);
            const isDealSigned = stepUi.isFinalMilestone ?? false;
            const canRewind =
              Boolean(onCompletedStepClick) && canRewindDealTrackerToStep(status, step, userType);
            const displayLabel =
              isCompleted && stepUi.labelCompleted != null
                ? stepUi.labelCompleted
                : isPending && stepUi.labelPending != null
                  ? stepUi.labelPending
                  : stepUi.label;
            const wrapsLabel = Boolean(STEP_LABEL_LINES[displayLabel]);
            const isAbove = isStartup && i % 2 === 1;
            // On a narrow card the first label is centered on the outer step and slides under the left corner.
            const pinStart = isStartup && i === 0;

            const circle = (
              <div
                className={[
                  'rounded-full flex items-center justify-center',
                  canRewind ? 'cursor-pointer hover:brightness-110' : undefined,
                ]
                  .filter(Boolean)
                  .join(' ')}
                style={{ width: CIRCLE_PX, height: CIRCLE_PX }}
                aria-current={isCurrent ? 'step' : undefined}
                data-step-completed={isCompleted ? 'true' : 'false'}
              >
                <TrackerCheck completed={isCompleted} />
              </div>
            );

            return (
              <div
                key={step}
                className="absolute top-0 h-full flex flex-col items-center"
                style={{
                  left: stepCenterLeft(i),
                  transform: 'translateX(-50%)',
                }}
                role="listitem"
              >
                <div
                  className="flex items-center justify-center"
                  style={{
                    height: isStartup ? (isDealSigned ? DEAL_SIGNED_PX : CIRCLE_PX) : ICON_ROW_PX,
                    marginTop: isStartup ? (isDealSigned ? 16 : 28) : 0,
                  }}
                >
                  {isDealSigned ? (
                    <DealSignedBadge completed={isCompleted} />
                  ) : canRewind ? (
                    <button
                      type="button"
                      className="p-0 bg-transparent border-0 disabled:cursor-default disabled:hover:brightness-100"
                      aria-label={`Go back to ${stepUi.labelCompleted ?? stepUi.label}`}
                      disabled={primaryActionDisabled || primaryActionLoading}
                      onClick={() => onCompletedStepClick?.(step)}
                    >
                      {circle}
                    </button>
                  ) : (
                    circle
                  )}
                </div>

                {!isDealSigned && (
                  <span
                    aria-label={displayLabel}
                    className={[
                      isStartup
                        ? [
                            'whitespace-nowrap text-[10px] sm:text-[11px] font-medium tracking-tight select-none pointer-events-none',
                            'absolute left-1/2 text-center',
                            pinStart
                              ? '-translate-x-[calc(50%-12px)] sm:-translate-x-1/2'
                              : '-translate-x-1/2',
                            isAbove ? 'top-[8px]' : 'top-[52px]',
                          ].join(' ')
                        : [
                            'block text-center text-[8px] sm:text-[9px] leading-[1.2] -mt-1',
                            wrapsLabel ? 'max-w-[4.75rem]' : 'whitespace-nowrap',
                          ].join(' '),
                      isCompleted
                        ? isStartup
                          ? 'text-[#92d050]'
                          : 'text-emerald-400'
                        : isCurrent
                          ? isStartup
                            ? 'text-white'
                            : 'text-gray-200'
                          : 'text-gray-400',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                  >
                    {isStartup ? displayLabel : <StepLabelText label={displayLabel} />}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {showButton && (
        <div className="mt-3 flex justify-center w-full">
          <div
            className={isStartup ? 'w-[72%] max-w-[220px]' : 'inline-flex'}
            data-follow-action={showFollowHover ? '' : undefined}
            onMouseEnter={event => {
              onPrimaryActionMouseEnter?.();
              if (!showFollowHover) return;
              const rect = event.currentTarget.getBoundingClientRect();
              setFollowTip({ top: rect.bottom + 8, left: rect.left + rect.width / 2 });
            }}
            onMouseLeave={() => {
              onPrimaryActionMouseLeave?.();
              setFollowTip(null);
            }}
          >
            <Button
              type="button"
              variant="outline"
              roundedFull
              fullWidth={isStartup}
              disabled={primaryActionDisabled || primaryActionLoading}
              title={primaryActionTooltip}
              className={`${
                isStartup
                  ? '!border-[0.5pt] !border-[#92d050] py-1 px-3.5 text-xs sm:text-[13px] font-semibold tracking-wide'
                  : dense
                    ? '!border-[0.5pt] !border-[#92d050] !py-2 px-5 text-sm font-medium lg:px-[2.90rem] lg:text-md [font-family:Rubik,sans-serif]'
                    : '!border-[0.5pt] !border-[#92d050] !py-2 px-[2.90rem] text-md font-medium [font-family:Rubik,sans-serif]'
              } flex items-center justify-center gap-2 whitespace-nowrap`}
              onClick={onPrimaryActionClick}
            >
              {primaryActionLoading && (
                <span className="inline-block w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
              )}
              <span>{buttonLabel}</span>
            </Button>
          </div>
          {followTip
            ? createPortal(
                <div
                  role="tooltip"
                  className="pointer-events-none w-[15.5rem] rounded-lg bg-white px-3 py-2 text-left text-sm font-medium leading-snug text-[#111111] shadow-lg"
                  style={{
                    position: 'fixed',
                    top: followTip.top,
                    left: followTip.left,
                    transform: 'translateX(-50%)',
                    zIndex: 2147483647,
                  }}
                >
                  {FOLLOW_HOVER}
                </div>,
                document.body
              )
            : null}
        </div>
      )}

      {/* Congratulations banner */}
      {!showButton && status === DEAL_STATUS.dealSigned && (
        <div className="mt-4 flex justify-center w-full">
          <div className="flex items-center gap-3 text-lg sm:text-xl font-semibold text-white">
            <img
              src="https://www.prairi.com/static/media/prairiwhite.d022d780e8a07b901ba6.png"
              alt="PRAIRI logo"
              className="w-7 h-7 sm:w-9 sm:h-9"
            />
            <span>Congratulations!</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default DealTracker;
